import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import { PromptModal } from '@/components'
import { Input } from '@/components_deprecated'

export interface IssueProcessChoiceFormItem {
  id?: string
  name: string
}

export interface IssueProcessForm {
  name: string
  choices: IssueProcessChoiceFormItem[]
}

interface ExistingProcessSummary {
  id: string
  name: string
}

interface IssueProcessPromptProps {
  initialData?: IssueProcessForm
  processId?: string
  existingProcesses: ExistingProcessSummary[]
  onSubmit: PromptResolve<IssueProcessForm | null>
}

export const IssueProcessPrompt: React.FC<IssueProcessPromptProps> = ({
  initialData,
  processId,
  existingProcesses,
  onSubmit,
}) => {
  const translations = useTranslate({
    addIssueProcess: 'settings.add-issue-process',
    editIssueProcess: 'settings.edit-issue-process',

    name: 'common.name',
    choices: 'settings.issue-process-choices',
    addChoice: 'settings.add-issue-process-choice',
    choiceName: 'settings.issue-process-choice-name',
    removeChoice: 'common.remove',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      required: 'common.required',
      nameExists: 'settings.validation.issue-process-name-exists',
      duplicateChoice: 'settings.validation.issue-process-choice-duplicate',
      noChoices: 'settings.validation.issue-process-no-choices',
    },
  })

  const editing = typeof initialData !== 'undefined'

  function validateName(name: string): string | null {
    if (name === '') return translations.validation.required
    const clash = existingProcesses.find(
      process => process.name === name && process.id !== processId
    )
    if (clash) return translations.validation.nameExists
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    updateForm,
    formChangeHandler: handler,
  } = useForm<IssueProcessForm>({
    values: initialData ?? { name: '', choices: [] },
    validators: {
      name: val => validateName(val),
      choices: val => {
        if (val.length === 0) return translations.validation.noChoices
        const names = val.map(c => c.name.trim()).filter(Boolean)
        if (new Set(names).size !== names.length)
          return translations.validation.duplicateChoice
        if (names.length !== val.length) return translations.validation.required
        return null
      },
    },
  })

  const [newChoiceName, setNewChoiceName] = useState('')

  function addChoice() {
    const trimmed = newChoiceName.trim()
    if (trimmed === '') return
    if (form.choices.some(c => c.name === trimmed)) return
    updateForm({ choices: [...form.choices, { name: trimmed }] })
    setNewChoiceName('')
  }

  function choicesEqual(
    a: IssueProcessChoiceFormItem[],
    b: IssueProcessChoiceFormItem[]
  ): boolean {
    if (a.length !== b.length) return false
    return a.every((c, i) => c.id === b[i]?.id && c.name === b[i]?.name)
  }

  const isDirty =
    !editing ||
    form.name !== initialData?.name ||
    !choicesEqual(form.choices, initialData.choices) ||
    newChoiceName.trim() !== ''

  function handleSubmit(values: IssueProcessForm) {
    const trimmed = newChoiceName.trim()
    if (trimmed !== '' && !values.choices.some(c => c.name === trimmed)) {
      onSubmit({ ...values, choices: [...values.choices, { name: trimmed }] })
      return
    }
    onSubmit(values)
  }

  function removeChoice(idx: number) {
    updateForm({ choices: form.choices.filter((_, i) => i !== idx) })
  }

  function updateChoiceName(idx: number, name: string) {
    updateForm({
      choices: form.choices.map((choice, i) =>
        i === idx ? { ...choice, name } : choice
      ),
    })
  }

  return (
    <PromptModal
      title={
        editing ? translations.editIssueProcess : translations.addIssueProcess
      }
      submitText={editing ? translations.update : translations.create}
      submitDisabled={
        errors.name != null ||
        form.name === '' ||
        (form.choices.length === 0 && newChoiceName.trim() === '') ||
        !isDirty
      }
      className="w-[480px]"
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      onSubmit={submit(handleSubmit)}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={event => {
          event.preventDefault()
          submit(handleSubmit)()
        }}
      >
        <div className="flex flex-col">
          <label className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.name}
          </label>
          <Input
            value={form.name}
            error={errors.name}
            autoFocus
            fullWidth
            onChange={handler('name')}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1.5 ml-1.5 text-sm font-semibold text-tertiary">
            {translations.choices}
          </label>
          {form.choices.length > 0 && (
            <ul className="m-0 mb-3 flex list-none flex-col gap-2 p-0">
              {form.choices.map((choice, idx) => (
                <li
                  key={choice.id ?? `new-${idx}`}
                  className="flex items-center gap-2"
                >
                  <Input
                    value={choice.name}
                    fullWidth
                    onChange={value => updateChoiceName(idx, value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeChoice(idx)}
                    aria-label={translations.removeChoice}
                    className="flex h-10 w-10 items-center justify-center rounded hover:bg-quaternary"
                  >
                    <Icon icon="times" color="gray6" hoverColor="red" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            <Input
              value={newChoiceName}
              fullWidth
              placeholder={translations.choiceName}
              onChange={setNewChoiceName}
              onEnter={evt => {
                evt.preventDefault()
                addChoice()
              }}
            />
            <button
              type="button"
              onClick={addChoice}
              aria-label={translations.addChoice}
              disabled={newChoiceName.trim() === ''}
              className="flex h-10 w-10 items-center justify-center rounded bg-secondary text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon icon="plus" color="white" />
            </button>
          </div>
          {errors.choices && (
            <span className="text-red mt-2 text-sm">{errors.choices}</span>
          )}
        </div>
      </form>
    </PromptModal>
  )
}
